
import Workout from "./Workout";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteWorkout,
  getWorkout,
  clearCurrentWorkout,
  createTemplateFromWorkout,
} from "../../redux/slices/workoutSlice";
import { getProfile } from "../../redux/slices/profileSlice";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { getRootDomain } from "../../utils/get_domain";
import { adminRequest } from "../../utils/adminApi";


const WorkoutContainer = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { token } = useSelector((state) => state.authSlice);
    // The admin tools below are env-aware: prod uses the authSlice token, staging uses
    // the held staging token and routes via ?env=staging.
    const { env: adminEnv, token: adminToken } = useSelector(adminRequest);
    const { workout_id } = useParams();
    const { workout } = useSelector((state) => state.workoutSlice);
    const [workoutActivities, setWorkoutActivities] = useState([]);
    const [bottomNavigationState, setBottomNavigationState] = useState(1);
    const [selectedIdForEdit, setSelectedIdForEdit] = useState(null);
    const [activityForEdit, setActivityForEdit] = useState(null);
    const [workoutStats, setWorkoutStats] = useState(null);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareLink, setShareLink] = useState("");
    const [adminCopyWorkoutToUserModalOpen, setAdminCopyWorkoutToUserModalOpen] = useState(false)
  
    useEffect(() => {
      return () => {
        clearCurrentWorkout()
      }
    }, []);
  
    useEffect(() => {
      if (workout_id) {
        dispatch(getWorkout({ token, workout_id }));
      }
    }, [workout_id, dispatch, token]);
  
    useEffect(() => {
      if (workout) {
        setWorkoutActivities(workout.allWorkoutActivities);
        setWorkoutStats(workout.stats);
      }
    }, [workout]);
  
    const handleDeleteWorkout = async () => {
      try {
        await dispatch(deleteWorkout({ token, workout_id }));
        navigate("/");
      } catch (error) {
        console.error("Failed to delete workout:", error);
      }
    };
  
    const handleFinishWorkout = () => {
      try {
        dispatch(clearCurrentWorkout());
        dispatch(getProfile({ token }));
        navigate("/");
      } catch (error) {
        console.error("Failed to finish workout:", error);
      }
    };
  
    const handleAddExerciseToWorkout = async (exercise) => {
      const result = await axios.post(
        `/api/exercise/createExercise`,
        {
          workout_id,
          exercise,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { exercise_id } = result.data;
      const savedExercise = {
        type: "exercise",
        parsed: `${exercise.reps} X ${exercise.sets} ${exercise.name} @ ${
          exercise.weight
        } ${exercise.exerciseUnit === 0 ? "lbs" : "kgs"}`,
        data: {
          exercise_id,
          name: exercise.name,
          sets: exercise.sets,
          reps: exercise.reps,
          weight: exercise.weight,
          weight_unit: exercise.exerciseUnit,
        },
      };
      setWorkoutActivities([...workoutActivities, savedExercise]);
      if (bottomNavigationState === 3) {
        setBottomNavigationState(1);
      }
    };
  
    const handleAddRowToWorkout = async (row) => {
      const result = await axios.post(
        `/api/exercise/createRow`,
        {
          workout_id,
          row,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { row_id } = result.data;
      const savedRow = {
        parsed: `${row.rowDistance}m row in ${row.rowTime}`,
        type: "row",
        data: {
          row_id,
        },
      };
      setWorkoutActivities([...workoutActivities, savedRow]);
      if (bottomNavigationState === 3) {
        setBottomNavigationState(1);
      }
    };
  
    const handleAddRunToWorkout = async (run) => {
      const result = await axios.post(
        `/api/exercise/createRun`,
        {
          workout_id,
          run,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const { run_id } = result.data;
      const savedRun = {
        parsed: `${run.runDistance} ${run.runDistanceType} run in ${run.runTime}`,
        type: "run",
        data: {
          run_id,
        },
      };
      setWorkoutActivities([...workoutActivities, savedRun]);
      if (bottomNavigationState === 3) {
        setBottomNavigationState(1);
      }
    };
  
    const handleCheckBoxChange = async (activity, completed, index) => {
      console.log("checkBoxChangeForActivity", activity, completed);
  
      let copy = [...workoutActivities];
      copy[index] = { ...copy[index], completed };
      setWorkoutActivities(copy);
  
      const result = await axios.post(
        `/api/exercise/completeActivity`,
        {
          activity,
          completed,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (result.status !== 200) {
        console.log("Error occured with checkbox - reverting checkbox state");
        let copy = [...workoutActivities];
        copy[index] = { ...copy[index], completed: !completed };
        setWorkoutActivities(copy);
      }
    };
  
    const handleCopyWorkout = () => {
      const parsedStrings = workoutActivities.reduce((workout, activity) => {
        return `${workout}${activity.parsed}\n`;
      }, "");
      navigator.clipboard
        .writeText(parsedStrings)
        .then((data) => {
          console.log("Workout copied to clipboard!", data);
          alert("Workout copied to clipboard!");
        })
        .catch((error) => {
          console.error("Failed to copy text to clipboard:", error);
        });
    };
  
    const handleCreateTemplateFromWorkout = () => {
      dispatch(createTemplateFromWorkout({ token, workout_id }));
      alert("Template created from workout.");
    };
  
    const handleCloseShareDialog = () => {
      setShareDialogOpen(false);
      setShareLink(null);
    };
  
    const handleShareWorkout = async (e) => {
      e.preventDefault();
      const result = await axios.post(
        `/api/workout/createWorkoutPublic`,
        {
          workout_id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const publicWorkoutId = result.data.workout_id;
  
      const root = getRootDomain();
      const link = `${root}/shared-workout/${publicWorkoutId}`;
      setShareLink(link);
      setShareDialogOpen(true);
    };
  
    const handleBottomNavigationChange = (value) => {
      setBottomNavigationState(value);
      if (selectedIdForEdit) {
        setSelectedIdForEdit(null);
        setActivityForEdit(null);
      }
    };
  
    const getActivityId = (activity) => {
      switch (activity.type) {
        case "exercise":
          return activity.data.exercise_id;
        case "run":
          return activity.data.run_id;
        case "row":
          return activity.data.row_id;
        default:
          console.log("not handled get activity id");
      }
    };
  
    const handleEditSelectChange = (activity) => {
      const id = getActivityId(activity);
      if (id === selectedIdForEdit) {
        setSelectedIdForEdit(null);
        setActivityForEdit(null);
      } else {
        setSelectedIdForEdit(id);
        setActivityForEdit(activity);
      }
    };
  
    const handleEditActivity = async (activity) => {
      await axios.post(
        `/api/exercise/editActivity`,
        {
          workout_id,
          activity,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActivityForEdit(null);
      setSelectedIdForEdit(null);
      if (activity.type === "exercise") {
        const { data: exercise } = activity;
        const savedExercise = {
          type: "exercise",
          parsed: `${exercise.reps} X ${exercise.sets} ${exercise.name} @ ${
            exercise.weight
          } ${exercise.exerciseUnit === 0 ? "lbs" : "kgs"}`,
          data: {
            ...exercise,
          },
        };
        const updatedActivities = workoutActivities.map((a) => {
          if (getActivityId(a) === exercise.exercise_id) {
            return savedExercise;
          } else {
            return a;
          }
        });
        setWorkoutActivities([...updatedActivities]);
      }
  
      if (activity.type === "run") {
        const { data: run } = activity;
        const updatedActivities = workoutActivities.map((a) => {
          if (getActivityId(a) === run.run_id) {
            return {
              parsed: `${run.distance} ${a.data.distance_type} run in ${run.time}`,
              type: "run",
              data: {
                ...a.data,
                ...run,
              },
            };
          } else {
            return a;
          }
        });
        setWorkoutActivities([...updatedActivities]);
      }
  
      if (activity.type === "row") {
        const { data: row } = activity;
        const updatedActivities = workoutActivities.map((a) => {
          if (getActivityId(a) === activity.data.row_id) {
            console.log("out", row, a);
            return {
              parsed: `${row.distance}m row in ${row.time}`,
              type: "row",
              data: {
                ...a.data,
                ...row,
              },
            };
          } else {
            return a;
          }
        });
        setWorkoutActivities([...updatedActivities]);
      }
    };

    const handleAdminCopyWorkoutToUser = () => {
      setAdminCopyWorkoutToUserModalOpen(true)
    }

    // Both admin tools now report their outcome through the return value rather than
    // console.log (issue #180) — the modal renders it, so a failure can no longer be
    // indistinguishable from a success. They also route through adminRequest, so they
    // honour the console's Prod/Staging toggle like every other /admin surface; the
    // `env` query param is routing-only and the Pages proxy strips it before
    // forwarding (functions/api/admin/*.js).
    const adminAuthHeaders = () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${adminToken}`,
    });

    const adminEnvQuery = adminEnv === "staging" ? "?env=staging" : "";

    const adminFailure = (error, fallback) => ({
      ok: false,
      message: error.response?.data?.error || error.message || fallback,
    });

    const handleAdminSubmitCopyWorkoutToUser = async (email) => {
      // adminRequest can hand back a null token on staging when no staging session is
      // held — fire nothing rather than a request with an empty credential.
      if (adminEnv === "staging" && !adminToken) {
        return { ok: false, message: "Log in to staging first." };
      }
      try {
        await axios.post(
          `/api/admin/copyWorkoutToUser${adminEnvQuery}`,
          {
            workout_id,
            email,
          },
          { headers: adminAuthHeaders() }
        );
        return {
          ok: true,
          message: `Copied workout ${workout_id} to ${email} on ${adminEnv}.`,
        };
      } catch (error) {
        console.error("Failed to copy workout to user:", error);
        return adminFailure(error, "Failed to copy workout to user.");
      }
    }

    const handleAdminDeleteWorkoutFromUser = async (workoutIdToDelete, email) => {
      if (adminEnv === "staging" && !adminToken) {
        return { ok: false, message: "Log in to staging first." };
      }
      try {
        await axios.post(
          `/api/admin/deleteWorkoutForUser${adminEnvQuery}`,
          {
            workout_id: workoutIdToDelete,
            email,
          },
          { headers: adminAuthHeaders() }
        );
        return {
          ok: true,
          message: `Deleted workout ${workoutIdToDelete} from ${email} on ${adminEnv}.`,
        };
      } catch (error) {
        console.error("Failed to delete workout for user:", error);
        return adminFailure(error, "Failed to delete workout for user.");
      }
    }

    const handleCloseCopyWorkoutToUserModal = () => {
      setAdminCopyWorkoutToUserModalOpen(false)
    }

  return (
    <Workout
      workout={workout}
      workoutActivities={workoutActivities}
      workoutStats={workoutStats}
      bottomNavigationState={bottomNavigationState}
      selectedIdForEdit={selectedIdForEdit}
      activityForEdit={activityForEdit}
      onCheckBoxChange={handleCheckBoxChange}
      onEditActivity={handleEditActivity}
      onEditSelectChange={handleEditSelectChange}
      onCopyWorkout={handleCopyWorkout}
      onBottomNavChange={handleBottomNavigationChange}
      onDeleteWorkout={handleDeleteWorkout}
      onFinishWorkout={handleFinishWorkout}
      onShareWorkout={handleShareWorkout}
      onCreateTemplate={handleCreateTemplateFromWorkout}
      onAddExercise={handleAddExerciseToWorkout}
      onAddRun={handleAddRunToWorkout}
      onAddRow={handleAddRowToWorkout}
      shareDialogOpen={shareDialogOpen}
      handleCloseShareDialog={handleCloseShareDialog}
      shareLink={shareLink}
      handleAdminCopyWorkoutToUser={handleAdminCopyWorkoutToUser}
      adminCopyWorkoutToUserModalOpen={adminCopyWorkoutToUserModalOpen}
      handleAdminSubmitCopyWorkoutToUser={handleAdminSubmitCopyWorkoutToUser}
      handleAdminDeleteWorkoutFromUser={handleAdminDeleteWorkoutFromUser}
      handleCloseCopyWorkoutToUserModal={handleCloseCopyWorkoutToUserModal}
    />
  );
};

export default WorkoutContainer;